"use client";

import { Fragment, type ReactNode } from "react";
import { usePathname } from "next/navigation";

import { isSensitivePaymentPath } from "@/lib/sensitive-route-policy";

// Routes that render as bare, single-decision app surfaces. /cart gets only a
// minimal "Secure checkout" header (see app/cart/layout.tsx); /chat gets its
// own slim consultation header; /embed renders embeddable widget surfaces.
// Global navigation, breadcrumbs, footer, mobile bottom bar, and the sticky
// global chat CTA would compete with or corrupt these surfaces.
const BARE_CHROME_PREFIXES = ["/cart", "/chat", "/embed"];

function isBareChrome(pathname: string | null): boolean {
  if (!pathname) return false;
  if (isSensitivePaymentPath(pathname)) return true;
  return BARE_CHROME_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function ChromeGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  if (isBareChrome(pathname)) return null;
  return <>{children}</>;
}

/**
 * Remounts its children (the root layout's `<main>`) whenever the chrome mode
 * flips between full chrome and a bare surface.
 *
 * A soft navigation into /chat removes the gated header (and breadcrumbs)
 * above `<main id="main-content">` seconds after the tap, outside CLS's 500 ms
 * input window. The SAME main node then moved up and scored 0.125 (0.17 with
 * breadcrumbs) — the #1 CLS breach target site-wide, 33% of human breach
 * beacons, on every entry path and on the way back out (C13, 2026-10-07). The
 * Layout Instability API only scores nodes present in both frames, so a NEW
 * main node in the commit that drops the chrome records no shift. The route
 * content is replaced in that same commit anyway, so the remount costs
 * nothing visible. Within one mode the key is stable and nothing remounts.
 */
export function ChromeModeBoundary({ children }: { children: ReactNode }) {
  const mode = isBareChrome(usePathname()) ? "bare" : "chrome";
  return <Fragment key={mode}>{children}</Fragment>;
}
